import {
  WSDataItemProps,
  WSTableCellProps
} from "@wingspanhq/fe-component-library";
import {
  IRequirementResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { QUERY_PAYER_ROWS } from "../../query/search/payer/queries/useQueryPayerRows";
import { getPayerRows, IPayerRow, PayerRowsQuery } from "../../services/search";
import { getPayerName } from "../Payers/utils/getPayerNames";
import { IDENTITY_VERIFICATION_REQUIREMENT_EVENT } from "./modules/IdentityVerification/configs/utils";
import { INSURANCE_COVERAGE_REQUIREMENT_EVENT } from "./modules/InsuranceCoverage/configs/utils";
import { SHARED_FILE_REQUIREMENT_EVENT } from "./modules/SharedFile/configs/utils";
import { SIGNATURE_REQUIREMENT_EVENT } from "./modules/Signature/configs/utils";
import { selectorRequirementEngagementsSize } from "./selectors/selectorRequirementEngagementsSize";
import {
  CellsConfig,
  ContextItem,
  CustomRequirement,
  ExtendedRequirementTableItemData,
  RequirementAdditionalData,
  RequirementContextType,
  RequirementFieldsConfig,
  RequirementTableRowConfig,
  TableCellProps
} from "./types";

export const ALL_REQUIREMENT_EVENTS = {
  OverrideStatus: "OverrideStatus",
  ResetStatus: "ResetStatus",
  ExtendExpirationDate: "ExtendExpirationDate",
  Renew: "Renew",
  ...SIGNATURE_REQUIREMENT_EVENT,
  ...SHARED_FILE_REQUIREMENT_EVENT,
  ...IDENTITY_VERIFICATION_REQUIREMENT_EVENT,
  ...INSURANCE_COVERAGE_REQUIREMENT_EVENT
} as const;

// export const REQUIREMENT_EVENTS: {
//   [key in RequirementType]: Record<
//     keyof typeof ALL_REQUIREMENT_EVENTS,
//     RequirementEvent
//   >;
// } = {
//   [RequirementType.Signature]: SIGNATURE_REQUIREMENT_EVENT,
//   [RequirementType.SharedFile]: SHARED_FILE_REQUIREMENT_EVENT
// };

export type RequirementEvent =
  (typeof ALL_REQUIREMENT_EVENTS)[keyof typeof ALL_REQUIREMENT_EVENTS];

export const LIST_OF_FIELDS: Array<
  keyof Omit<
    RequirementFieldsConfig<any>,
    "isLoading" | "dataUpdatedAt" | "getAdditionalData"
  >
> = [
  "requirementName",
  "requirementInstructions",
  "requirementDetailsProgressWidget",
  "isActionRequired",
  "requirementInstructionsWidgets",
  "requirementStatusAlert",
  "requirementDetails",
  "requirementBasicInformation",
  "requirementDetailsWidgets",
  "requirementAdditionalContent",
  "toolbarActions",
  "nameCell",
  "typeCell",
  "statusCell",
  "clientsCell",
  "engagementsCell"
];

export const sharedRequirementsQueriesConfig = {
  refetchOnMount: (query: any) => {
    return query.state.isInvalidated;
  },
  retry: false,
  retryOnMount: true
};

export const buildConfigFields = <
  AdditionalData extends RequirementAdditionalData,
  RT extends RequirementType
>(props: {
  payerConfig: CellsConfig<IRequirementResponse, ContextItem, AdditionalData>;
  payeeConfig: CellsConfig<IRequirementResponse, ContextItem, AdditionalData>;
  getAdditionalData: (
    requirement: IRequirementResponse,
    context: ContextItem
  ) => AdditionalData | undefined;
}) => {
  const errorCell: TableCellProps = {
    text: "--",
    secondaryText: "error loading",
    secondaryTextColor: "amber400"
  };

  return () => {
    const fields: RequirementTableRowConfig[RT] =
      {} as RequirementTableRowConfig[RT];

    LIST_OF_FIELDS.forEach(key => {
      fields[key] = {
        [RequirementContextType.Payer]: (requirement, context) => {
          const document = props.getAdditionalData(requirement, context);

          if (!document && ["statusCell"].includes(key)) {
            return errorCell; // Return error for critical fields if no document
          }

          return props.payerConfig[key]?.(
            requirement,
            context,
            document!
          ) as any;
        },
        [RequirementContextType.Payee]: (requirement, context) => {
          const document = props.getAdditionalData(requirement, context);

          if (!document && ["statusCell"].includes(key)) {
            return errorCell; // Return error for critical fields if no document
          }

          return props.payeeConfig[key]?.(
            requirement,
            context,
            document!
          ) as any;
        }
      };
    });

    return fields;
  };
};

export function getIsCustomRequirement(
  requirement: ExtendedRequirementTableItemData
): requirement is CustomRequirement {
  return !("requirementId" in requirement);
}

export function getRequirementListKey(
  requirement: ExtendedRequirementTableItemData,
  index: number
) {
  if (getIsCustomRequirement(requirement)) {
    return index.toString();
  }

  return requirement.requirementId;
}

export function getIsGeneralRequirement(requirement: IRequirementResponse) {
  return [
    RequirementType.Registration,
    RequirementType.PayoutMethod,
    RequirementType.TaxVerification
  ].includes(requirement.requirementType);
}

export function getRequirementPayerDataItemProps(
  requirement: IRequirementResponse,
  payer?: IPayerRow
): WSDataItemProps {
  const payerName = payer ? getPayerName(payer) : undefined;

  return {
    label: "Clients",
    value: payerName,
    secondaryText: `${selectorRequirementEngagementsSize(
      requirement
    )} engagement(s)`
  };
}

export function getRequirementPayerTableCellProps(
  requirement: IRequirementResponse,
  payer?: IPayerRow
): WSTableCellProps {
  const payerName = payer ? getPayerName(payer) : undefined;

  return {
    text: payerName,
    secondaryText: `${selectorRequirementEngagementsSize(
      requirement
    )} engagement(s)`
  };
}

export function getRequirementEngagementsDataItemProps(
  requirement: IRequirementResponse
): WSDataItemProps {
  return {
    label: "Engagements",
    value: selectorRequirementEngagementsSize(requirement)
  };
}

export function getRequirementEngagementsTableCellProps(
  requirement: IRequirementResponse
): WSTableCellProps {
  return {
    text: String(selectorRequirementEngagementsSize(requirement))
  };
}

export function getRequirementPayerRowQueries(
  requirements: IRequirementResponse[],
  requirementType: RequirementType
) {
  return requirements
    .filter(
      requirement =>
        requirement.requirementType === requirementType && !!requirement.payerId
    )
    .map(requirement => {
      const requestQuery: PayerRowsQuery = {
        filter: {
          payerId: requirement.payerId
        }
      };

      return {
        queryKey: [QUERY_PAYER_ROWS, requestQuery],
        queryFn: () => getPayerRows(requestQuery),
        ...sharedRequirementsQueriesConfig
      };
    });
}

export { sortRequirementsByPriority } from "./utils/sortRequirementsByPriority";
